import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Button, { Colors, Variants } from '../../atoms/Button';
import TextInput from '../../atoms/TextInput';
import './TypeToConfirmDialog.scss';

/**
 * A confirmation dialog that requires the user to type a specific number before
 * the Confirm button becomes enabled. Used for high-impact bulk actions where the
 * user should be aware of exactly how many records will be affected.
 */
function TypeToConfirmDialog({
  open = false,
  count = 0,
  title = 'Confirm action',
  message = '',
  confirmLabel = 'Confirm',
  confirmColor = Colors.Danger,
  onCancel = () => {},
  onConfirm = () => {},
}) {
  // --- State hooks ---
  const [typedValue, setTypedValue] = useState('');

  // --- Atoms and other hooks ---
  const dialogRef = useRef(null);

  // --- Navigation/side effects ---
  useEffect(() => {
    if (dialogRef.current) {
      if (open) {
        setTypedValue('');
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }
  }, [open]);

  // --- Derived state ---
  const isConfirmEnabled = typedValue === String(count);

  // --- Function definitions ---
  const handleCancel = () => {
    setTypedValue('');
    onCancel();
  };

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    setTypedValue('');
    onConfirm();
  };

  const renderMessage = () => {
    if (typeof message === 'string') {
      return message.split('\n').map((line, i) => (
        <div key={i} className="TypeToConfirmDialog-messageLine">
          {line}
        </div>
      ));
    }
    return message;
  };

  return ReactDOM.createPortal(
    <dialog ref={dialogRef} className="TypeToConfirmDialog">
      <h3 className="TypeToConfirmDialog-title">{title}</h3>

      {message && <div className="TypeToConfirmDialog-message">{renderMessage()}</div>}

      <div className="TypeToConfirmDialog-prompt">
        Type <strong>{count}</strong> to confirm:
      </div>
      <TextInput
        autoFocus
        value={typedValue}
        placeholder={String(count)}
        onChange={(e) => setTypedValue(e.target.value)}
      />

      <div className="TypeToConfirmDialog-buttons">
        <Button content="Cancel" onClick={handleCancel} />
        <Button
          content={confirmLabel}
          variant={Variants.Vivid}
          color={confirmColor}
          disabled={!isConfirmEnabled}
          onClick={handleConfirm}
        />
      </div>
    </dialog>,
    document.getElementById('portal-root'),
  );
}

TypeToConfirmDialog.propTypes = {
  open: PropTypes.bool,
  count: PropTypes.number.isRequired,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmLabel: PropTypes.string,
  confirmColor: PropTypes.oneOf(Object.values(Colors)),
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default TypeToConfirmDialog;
